import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownMenu } from '../../hooks/useDropdownMenu';

/** How a {@link DropdownMenu} is opened. */
export type DropdownMenuOpenOn = 'click' | 'hover';

/** Handed to {@link DropdownMenuProps.renderTrigger} to wire up the trigger button. */
export interface DropdownMenuTrigger {
  /** Whether the menu is currently open (e.g. for `aria-expanded`). */
  open: boolean;
  /** Attach to the trigger `<button>` — drives positioning and outside-click detection. */
  ref: React.Ref<HTMLButtonElement>;
  /** Toggle the menu open/closed. No-ops while `disabled`. */
  toggle: () => void;
}

/** Handed to the `children` render-prop so menu items can close the menu. */
export interface DropdownMenuApi {
  /** Close the menu (e.g. after activating an item). */
  close: () => void;
}

export interface DropdownMenuProps {
  /** Render the trigger button. Must attach `ref`; usually wires `toggle` to `onClick`. */
  renderTrigger: (trigger: DropdownMenuTrigger) => React.ReactNode;
  /** Menu body, as a render-prop so items can `close()` the menu when activated. */
  children: (api: DropdownMenuApi) => React.ReactNode;
  /** How the menu opens. `'click'` (default) or `'hover'` (adds the hover-bridge). */
  openOn?: DropdownMenuOpenOn;
  /** When true, the menu cannot be opened by click or hover. */
  disabled?: boolean;
  /** Class on the outer wrapper element. */
  className?: string;
  /** Extra class added to the wrapper while the menu is open (e.g. an `--open` modifier). */
  openClassName?: string;
  /** Class on the portaled menu element. */
  menuClassName?: string;
  /** ARIA role for the menu container (e.g. `'menu'`). Omitted → no role attribute. */
  menuRole?: React.AriaRole;
  /** Grace period (ms) before closing after the pointer leaves, in hover mode. Default 150. */
  hoverCloseDelay?: number;
}

/**
 * A single row-actions dropdown: a trigger button plus a menu that is portaled into
 * `<body>` (fixed-position) so it can never be clipped by the table / mobile-card
 * containers that hold the trigger — the fix for the off-screen mobile bug (issue #325).
 *
 * The open/close + on-screen positioning mechanics come from {@link useDropdownMenu}; this
 * component adds the shared markup (wrapper + portaled menu), a `renderTrigger` slot, and an
 * optional hover-bridge (`openOn="hover"`), so the whole control is reusable across pages
 * (Business Email, Managed WordPress / cPanel, Manage Packages). Each consumer keeps its own
 * class names and item markup; only the mechanics are shared.
 *
 * Usage:
 *   <DropdownMenu
 *     className="hi-ui-foo__wrapper"
 *     menuClassName="hi-ui-foo__menu"
 *     renderTrigger={({ open, ref, toggle }) => (
 *       <button ref={ref} aria-expanded={open} onClick={toggle}>…</button>
 *     )}
 *   >
 *     {({ close }) => <button onClick={() => { close(); doThing(); }}>Do thing</button>}
 *   </DropdownMenu>
 */
export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  renderTrigger,
  children,
  openOn = 'click',
  disabled = false,
  className,
  openClassName,
  menuClassName,
  menuRole,
  hoverCloseDelay = 150,
}) => {
  const { open, triggerRef, menuRef, menuStyle, openMenu, closeMenu } = useDropdownMenu();
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverClose = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    clearHoverClose();
    hoverTimeout.current = setTimeout(() => closeMenu(), hoverCloseDelay);
  }, [clearHoverClose, closeMenu, hoverCloseDelay]);

  // Cancel a pending hover-close if the control unmounts mid-timeout.
  useEffect(() => clearHoverClose, [clearHoverClose]);

  const openFromTrigger = useCallback(() => {
    /* v8 ignore next -- the ref is always attached by the time a handler fires */
    if (!triggerRef.current) return;
    openMenu(triggerRef.current);
  }, [openMenu, triggerRef]);

  const toggle = useCallback(() => {
    if (disabled) return;
    if (open) closeMenu();
    else openFromTrigger();
  }, [disabled, open, closeMenu, openFromTrigger]);

  const hover = openOn === 'hover';

  const wrapperHoverProps = hover
    ? {
        onMouseEnter: () => {
          if (disabled) return;
          clearHoverClose();
          openFromTrigger();
        },
        onMouseLeave: () => {
          if (open) scheduleHoverClose();
        },
      }
    : {};

  const menuHoverProps = hover
    ? { onMouseEnter: clearHoverClose, onMouseLeave: scheduleHoverClose }
    : {};

  const wrapperClassName =
    [className, open ? openClassName : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <div className={wrapperClassName} {...wrapperHoverProps}>
      {renderTrigger({ open, ref: triggerRef, toggle })}
      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            className={menuClassName}
            role={menuRole}
            style={menuStyle}
            {...menuHoverProps}
          >
            {children({ close: closeMenu })}
          </div>,
          document.body
        )}
    </div>
  );
};
